import { FormEvent, useEffect, useMemo, useState, type ReactNode } from "react";
import { supabase } from "./lib/supabase";
import { runtimeConfig } from "./lib/runtime-config";
import {
  copy,
  formatCurrency,
  formatDate,
  regionalDefaults,
  type SupportedCountry,
  type SupportedCurrency,
  type SupportedLocale,
} from "./localization";
import { CommercialWorkspace, TaskCenter } from "./CommercialWorkspace";
import { UserManagement } from "./UserManagement";
import { PlanManagement } from "./PlanManagement";
import { VersionCenter } from "./VersionCenter";
import { SiteTemplateCatalog } from "./SiteTemplateCatalog";
import { PlatformSupportCenter } from "./PlatformSupportCenter";
import { PlatformOperationsCenter } from "./PlatformOperationsCenter";
import { PlatformDefaultDocuments } from "./PlatformDefaultDocuments";
import { ThemeToggle } from "./ThemeToggle";
import "./commercial.css";
import "./task-center.css";
import "./conversion-wizard.css";
import "./platform-settings.css";

export type DashboardProfile = {
  user_id: string;
  display_name: string | null;
  locale: SupportedLocale;
  country_code: SupportedCountry;
  currency_code: SupportedCurrency;
  timezone: string;
};
export type DashboardMembership = {
  role_key:
    | "super_master"
    | "platform_master"
    | "commercial_manager"
    | "commercial";
  permissions: string[];
  active: boolean;
};
type Organization = {
  id: string;
  name: string;
  legal_name: string | null;
  slug: string;
  status: string;
  commercial_status: string;
  country_code: SupportedCountry;
  locale: SupportedLocale;
  currency_code: SupportedCurrency;
  timezone: string;
  tax_identifier: string | null;
  email: string | null;
  phone: string | null;
  website: string | null;
  city: string | null;
  postal_code: string | null;
  address_line: string | null;
  district: string | null;
  notes: string | null;
  archived_at: string | null;
  created_at: string;
};
type Branch = {
  id: string;
  name: string;
  code: string;
  is_headquarters: boolean;
  active: boolean;
  city: string | null;
  phone: string | null;
};
type Activity = {
  id: string;
  activity_type: string;
  subject: string;
  body: string | null;
  created_at: string;
};
type SubscriptionPlan = {
  id: string;
  name: string;
  trial_days: number | null;
  currency_code: string;
  price_cents: number | null;
  billing_interval: string | null;
  is_trial: boolean;
};
type StandSubscription = {
  id: string;
  plan_id: string;
  status: string;
  effective_status: string;
  starts_at: string | null;
  trial_ends_at: string | null;
  current_period_end: string | null;
  auto_renew: boolean;
  status_reason: string | null;
  terms_snapshot: Record<string, unknown>;
};
type SubscriptionEvent = {
  id: number;
  event_type: string;
  message: string | null;
  created_at: string;
};
type View =
  | "overview"
  | "commercial"
  | "clients"
  | "users"
  | "plans"
  | "versions"
  | "support"
  | "operations"
  | "settings";
const environment = runtimeConfig.appEnv;
const statusLabels: Record<string, Record<SupportedLocale, string>> = {
  prospect: { "pt-PT": "Prospecto", "pt-BR": "Prospect" },
  trial: { "pt-PT": "Em teste", "pt-BR": "Em teste" },
  customer: { "pt-PT": "Cliente", "pt-BR": "Cliente" },
  suspended: { "pt-PT": "Suspenso", "pt-BR": "Suspenso" },
  cancelled: { "pt-PT": "Cancelado", "pt-BR": "Cancelado" },
};
const statusDescriptions: Record<string, Record<SupportedLocale, string>> = {
  prospect: {
    "pt-PT":
      "Stand identificado ou em contacto comercial, ainda sem período de teste nem plano ativo.",
    "pt-BR":
      "Loja identificada ou em contato comercial, ainda sem período de teste nem plano ativo.",
  },
  trial: {
    "pt-PT":
      "Stand a experimentar ou a configurar o Vorno Stand antes de contratar um plano.",
    "pt-BR":
      "Loja experimentando ou configurando o Vorno Stand antes de contratar um plano.",
  },
  customer: {
    "pt-PT":
      "Stand com relação comercial ativa e acesso a um plano do Vorno Stand.",
    "pt-BR":
      "Loja com relação comercial ativa e acesso a um plano do Vorno Stand.",
  },
  suspended: {
    "pt-PT":
      "Relação ou acesso temporariamente suspenso, com possibilidade de reativação.",
    "pt-BR":
      "Relação ou acesso temporariamente suspenso, com possibilidade de reativação.",
  },
  cancelled: {
    "pt-PT":
      "Relação comercial terminada. A ficha e o histórico são preservados para consulta.",
    "pt-BR":
      "Relação comercial encerrada. O cadastro e o histórico são preservados para consulta.",
  },
};
const slugify = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
const normalizeWebsite = (value: FormDataEntryValue | null) => {
  const website = String(value ?? "").trim();
  if (!website) return null;
  if (/^https?:\/\//i.test(website)) return website;
  if (/^www\./i.test(website)) return `https://${website}`;
  return `https://www.${website}`;
};
function Brand() {
  return (
    <div className="brand">
      <span className="brand-mark" aria-hidden="true">
        <i />
      </span>
      <span>
        <strong>Vorno</strong>
        <small>Stand</small>
      </span>
    </div>
  );
}

function OrganizationForm({
  locale,
  onClose,
  onCreated,
}: {
  locale: SupportedLocale;
  onClose: () => void;
  onCreated: (organization: Organization) => void;
}) {
  const t = copy(locale),
    [country, setCountry] = useState<SupportedCountry>("PT"),
    [saving, setSaving] = useState(false),
    [error, setError] = useState<string | null>(null),
    defaults = regionalDefaults[country];
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    const form = new FormData(event.currentTarget),
      name = String(form.get("name") ?? "").trim();
    if (name.length < 2) {
      setSaving(false);
      setError(
        locale === "pt-BR"
          ? "Informe um nome válido."
          : "Indique um nome válido.",
      );
      return;
    }
    const payload = {
      name,
      slug: `${slugify(name)}-${Date.now().toString(36)}`,
      legal_name: String(form.get("legal_name") || "") || null,
      commercial_status: String(form.get("commercial_status") || "prospect"),
      country_code: country,
      locale: defaults.locale,
      currency_code: defaults.currencyCode,
      timezone: defaults.timezone,
      tax_identifier: String(form.get("tax_identifier") || "") || null,
      email: String(form.get("email") || "") || null,
      phone: String(form.get("phone") || "") || null,
      website: normalizeWebsite(form.get("website")),
      city: String(form.get("city") || "") || null,
      postal_code: String(form.get("postal_code") || "") || null,
      address_line: String(form.get("address_line") || "") || null,
      notes: String(form.get("notes") || "") || null,
    };
    const { data, error: insertError } = await supabase
      .from("organizations")
      .insert(payload)
      .select("*")
      .single();
    setSaving(false);
    if (insertError || !data) {
      setError(insertError?.message ?? "Não foi possível criar o registo.");
      return;
    }
    onCreated(data as Organization);
  };
  return (
    <div className="modal-backdrop">
      <section className="crm-modal" role="dialog" aria-modal="true">
        <header>
          <div>
            <p className="eyebrow">{t.crm}</p>
            <h2>{t.newOrganization}</h2>
          </div>
          <button className="icon-button" onClick={onClose}>
            ×
          </button>
        </header>
        <form onSubmit={submit}>
          <div className="form-grid">
            <label>
              <span>{t.name} *</span>
              <input name="name" autoFocus required minLength={2} />
            </label>
            <label>
              <span>{t.legalName}</span>
              <input name="legal_name" />
            </label>
            <label>
              <span>{t.country} *</span>
              <select
                value={country}
                onChange={(e) => setCountry(e.target.value as SupportedCountry)}
              >
                <option value="PT">Portugal</option>
                <option value="BR">Brasil</option>
              </select>
            </label>
            <label>
              <span>{t.status}</span>
              <select name="commercial_status">
                <option value="prospect">
                  {statusLabels.prospect[locale]}
                </option>
                <option value="trial">{statusLabels.trial[locale]}</option>
                <option value="customer">
                  {statusLabels.customer[locale]}
                </option>
              </select>
            </label>
            <label>
              <span>{t.taxId}</span>
              <input name="tax_identifier" />
            </label>
            <label>
              <span>{t.city}</span>
              <input name="city" />
            </label>
            <label>
              <span>Código postal</span>
              <input name="postal_code" />
            </label>
            <label>
              <span>{t.email}</span>
              <input name="email" type="email" />
            </label>
            <label>
              <span>{t.phone}</span>
              <input name="phone" type="tel" />
            </label>
            <label>
              <span>{t.website}</span>
              <input name="website" type="url" placeholder="https://" />
            </label>
            <label className="wide">
              <span>Morada</span>
              <input name="address_line" />
            </label>
            <label className="wide">
              <span>{t.notes}</span>
              <textarea name="notes" rows={3} />
            </label>
          </div>
          <div className="regional-preview">
            <span>{defaults.locale}</span>
            <span>{defaults.currencyCode}</span>
            <span>{defaults.timezone}</span>
            <strong>
              {formatCurrency(1250, defaults.locale, defaults.currencyCode)}
            </strong>
          </div>
          {error && <p className="form-error">{error}</p>}
          <footer>
            <button className="text-button" type="button" onClick={onClose}>
              {t.cancel}
            </button>
            <button className="primary-button" disabled={saving}>
              {saving ? "…" : t.save}
            </button>
          </footer>
        </form>
      </section>
    </div>
  );
}

const subscriptionLabels: Record<string, string> = {
  pending: "Pendente",
  trialing: "Em degustação",
  active: "Ativa",
  read_only: "Apenas leitura",
  suspended: "Suspensa",
  cancelled: "Cancelada",
};
const subscriptionEventLabels: Record<string, string> = {
  activated: "Plano ativado",
  trial_started: "Degustação iniciada",
  plan_changed: "Plano alterado",
  suspended: "Subscrição suspensa",
  cancelled: "Subscrição cancelada",
  reactivated: "Subscrição reativada",
  read_only: "Acesso limitado a leitura",
};
type SubscriptionAction =
  | "activate"
  | "trial"
  | "suspend"
  | "cancel"
  | "reactivate";
const subscriptionActionHelp: Record<SubscriptionAction, string> = {
  activate:
    "Ativa o plano escolhido imediatamente. Se já existir uma subscrição, o plano atual será substituído.",
  trial:
    "Inicia um período experimental com a duração e os limites definidos no plano escolhido.",
  suspend:
    "Interrompe temporariamente a atividade do stand. O histórico e os dados permanecem guardados.",
  cancel:
    "Termina a subscrição. O motivo fica registado no histórico para consulta futura.",
  reactivate:
    "Devolve o acesso operacional ao stand usando a subscrição que foi temporariamente suspensa.",
};

function SubscriptionPanel({
  organization,
  locale,
  canManage,
  onOrganizationUpdated,
}: {
  organization: Organization;
  locale: SupportedLocale;
  canManage: boolean;
  onOrganizationUpdated: (organization: Organization) => void;
}) {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]),
    [subscription, setSubscription] = useState<StandSubscription | null>(null),
    [events, setEvents] = useState<SubscriptionEvent[]>([]),
    [selectedPlan, setSelectedPlan] = useState(""),
    [selectedAction, setSelectedAction] =
      useState<SubscriptionAction>("activate"),
    [note, setNote] = useState(""),
    [autoRenew, setAutoRenew] = useState(true),
    [saving, setSaving] = useState(false),
    [error, setError] = useState<string | null>(null);
  const load = async () => {
    const [p, s, e] = await Promise.all([
      supabase
        .from("plans")
        .select(
          "id,name,trial_days,currency_code,price_cents,billing_interval,is_trial",
        )
        .eq("status", "active")
        .order("sort_order"),
      supabase
        .from("organization_subscription_access")
        .select(
          "id,plan_id,status,effective_status,starts_at,trial_ends_at,current_period_end,auto_renew,status_reason,terms_snapshot",
        )
        .eq("organization_id", organization.id)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle(),
      supabase
        .from("subscription_events")
        .select("id,event_type,message,created_at")
        .eq("organization_id", organization.id)
        .order("created_at", { ascending: false })
        .limit(30),
    ]);
    setPlans((p.data ?? []) as SubscriptionPlan[]);
    setSubscription((s.data ?? null) as StandSubscription | null);
    setEvents((e.data ?? []) as SubscriptionEvent[]);
    if (!selectedPlan && p.data?.[0]) setSelectedPlan(String(p.data[0].id));
    const current = (s.data ?? null) as StandSubscription | null;
    setSelectedAction(
      current?.effective_status === "suspended" ? "reactivate" : "activate",
    );
  };
  useEffect(() => {
    void load();
  }, [organization.id]);
  useEffect(() => {
    if (selectedAction !== "activate" && selectedAction !== "trial") return;
    const eligible = plans.filter((plan) =>
      selectedAction === "trial" ? plan.is_trial : !plan.is_trial,
    );
    if (!eligible.some((plan) => plan.id === selectedPlan))
      setSelectedPlan(eligible[0]?.id ?? "");
  }, [plans, selectedAction, selectedPlan]);
  const execute = async (action: SubscriptionAction) => {
    if ((action === "activate" || action === "trial") && !selectedPlan) {
      setError("Escolha primeiro um plano.");
      return;
    }
    if ((action === "suspend" || action === "cancel") && !note.trim()) {
      setError("Indique o motivo desta alteração.");
      return;
    }
    setSaving(true);
    setError(null);
    const { error } = await supabase.rpc("manage_organization_subscription", {
      p_organization_id: organization.id,
      p_action: action,
      p_plan_id:
        action === "activate" || action === "trial" ? selectedPlan : null,
      p_note: note.trim() || null,
      p_auto_renew: autoRenew,
    });
    setSaving(false);
    if (error) {
      setError(error.message);
      return;
    }
    setNote("");
    await load();
    const { data } = await supabase
      .from("organizations")
      .select("*")
      .eq("id", organization.id)
      .single();
    if (data) onOrganizationUpdated(data as Organization);
  };
  const chosen = plans.find((plan) => plan.id === selectedPlan),
    snapshot = subscription?.terms_snapshot ?? {},
    planName = String(
      snapshot.name ?? chosen?.name ?? "Plano não identificado",
    ),
    price = Number(snapshot.price_cents ?? chosen?.price_cents ?? 0),
    currency = String(
      snapshot.currency_code ??
        chosen?.currency_code ??
        organization.currency_code,
    ),
    region = currency === "BRL" ? "pt-BR" : "pt-PT";
  const actionNeedsPlan =
      selectedAction === "activate" || selectedAction === "trial",
    actionNeedsReason =
      selectedAction === "suspend" || selectedAction === "cancel",
    actionLabels: Record<SubscriptionAction, string> = {
      activate: subscription ? "Ativar outro plano" : "Ativar plano",
      trial: "Iniciar degustação",
      suspend: "Suspender acesso",
      cancel: "Cancelar subscrição",
      reactivate: "Reativar acesso",
    };
  return (
    <div className="detail-body subscription-panel">
      <div className="subscription-intro">
        <div>
          <p className="eyebrow">Acesso do stand</p>
          <h3>{subscription ? planName : "Sem plano ativo"}</h3>
          <p>
            {subscription
              ? "As condições desta subscrição estão preservadas na versão contratada."
              : "Escolha um plano para ativar o acesso operacional ou iniciar uma degustação."}
          </p>
        </div>
        <span
          className={`subscription-state ${subscription?.effective_status ?? "none"}`}
        >
          {subscriptionLabels[subscription?.effective_status ?? ""] ??
            "Sem subscrição"}
        </span>
      </div>
      {subscription && (
        <div className="subscription-facts">
          <article>
            <small>Início</small>
            <strong>
              {subscription.starts_at
                ? formatDate(subscription.starts_at, locale)
                : "—"}
            </strong>
          </article>
          <article>
            <small>
              {subscription.status === "trialing"
                ? "Fim da degustação"
                : "Próxima renovação"}
            </small>
            <strong>
              {subscription.trial_ends_at || subscription.current_period_end
                ? formatDate(
                    subscription.trial_ends_at ??
                      subscription.current_period_end!,
                    locale,
                  )
                : "Sem data definida"}
            </strong>
          </article>
          <article>
            <small>Renovação automática</small>
            <strong>{subscription.auto_renew ? "Ativa" : "Desativada"}</strong>
          </article>
          <article>
            <small>Valor contratado</small>
            <strong>
              {new Intl.NumberFormat(region, {
                style: "currency",
                currency,
              }).format(price / 100)}
            </strong>
          </article>
        </div>
      )}
      {canManage ? <section className="subscription-control">
        <div>
          <h3>Estado do acesso</h3>
          <p>
            Escolha o resultado pretendido. Apenas uma opção pode ser aplicada
            de cada vez e todas as alterações ficam registadas no histórico.
          </p>
        </div>
        <div className="subscription-form">
          <label className="wide">
            <span>O que pretende fazer? *</span>
            <select
              value={selectedAction}
              onChange={(event) =>
                setSelectedAction(event.target.value as SubscriptionAction)
              }
            >
              <option value="activate">
                {subscription ? "Ativar ou alterar plano" : "Ativar plano"}
              </option>
              <option value="trial">Iniciar degustação</option>
              {subscription &&
                !["suspended", "read_only", "cancelled"].includes(
                  subscription.effective_status,
                ) && <option value="suspend">Suspender acesso</option>}
              {subscription?.effective_status === "suspended" && (
                <option value="reactivate">Reativar acesso</option>
              )}
              {subscription?.effective_status !== "cancelled" &&
                subscription && (
                  <option value="cancel">Cancelar subscrição</option>
                )}
            </select>
          </label>
          <div className={`subscription-action-help action-${selectedAction}`}>
            <strong>{actionLabels[selectedAction]}</strong>
            <span>{subscriptionActionHelp[selectedAction]}</span>
          </div>
          {actionNeedsPlan && (
            <label>
              <span>Plano *</span>
              <select
                value={selectedPlan}
                onChange={(event) => setSelectedPlan(event.target.value)}
              >
                <option value="">Escolher plano</option>
                {plans
                  .filter((plan) =>
                    selectedAction === "trial" ? plan.is_trial : !plan.is_trial,
                  )
                  .map((plan) => (
                    <option key={plan.id} value={plan.id}>
                      {plan.name}
                    </option>
                  ))}
              </select>
            </label>
          )}
          {(selectedAction === "activate" ||
            selectedAction === "reactivate") && (
            <label className="auto-renew">
              <input
                type="checkbox"
                checked={autoRenew}
                onChange={(event) => setAutoRenew(event.target.checked)}
              />
              <span>Renovação automática</span>
            </label>
          )}
          <label className="wide">
            <span>{actionNeedsReason ? "Motivo *" : "Nota opcional"}</span>
            <textarea
              rows={2}
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder={
                actionNeedsReason
                  ? "Explique por que motivo está a tomar esta decisão"
                  : "Acrescente informação útil para o histórico"
              }
            />
          </label>
        </div>
        {chosen && actionNeedsPlan && (
          <p className="plan-selection-note">
            {chosen.trial_days ?? 7} dias de degustação ·{" "}
            {new Intl.NumberFormat(
              chosen.currency_code === "BRL" ? "pt-BR" : "pt-PT",
              { style: "currency", currency: chosen.currency_code },
            ).format((chosen.price_cents ?? 0) / 100)}{" "}
            ·{" "}
            {chosen.billing_interval === "yearly"
              ? "anual"
              : chosen.billing_interval === "monthly"
                ? "mensal"
                : "pagamento único"}
          </p>
        )}
        {error && <p className="form-error">{error}</p>}
        <div className="subscription-actions">
          <button
            className={`compact ${selectedAction === "cancel" ? "danger-button" : "primary-button"}`}
            disabled={saving}
            onClick={() => void execute(selectedAction)}
          >
            {saving ? "A aplicar…" : actionLabels[selectedAction]}
          </button>
        </div>
      </section> : (
        <section className="subscription-control">
          <div>
            <h3>Consulta da subscrição</h3>
            <p>A alteração de planos está reservada ao Super Master ou a um Master com a permissão “Gerir planos”.</p>
          </div>
        </section>
      )}
      <section className="subscription-history">
        <h3>Histórico da subscrição</h3>
        {events.length === 0 ? (
          <p className="muted">Ainda não existem alterações registadas.</p>
        ) : (
          events.map((event) => (
            <article key={event.id}>
              <i />
              <div>
                <strong>
                  {subscriptionEventLabels[event.event_type] ??
                    event.event_type}
                </strong>
                <span>
                  {event.message || "Alteração registada pelo sistema."}
                </span>
              </div>
              <time>{formatDate(event.created_at, locale)}</time>
            </article>
          ))
        )}
      </section>
    </div>
  );
}

function OrganizationDetail({
  organization,
  userId,
  locale,
  canManageSubscriptions,
  onClose,
  onUpdated,
  onOpenStand,
}: {
  organization: Organization;
  userId: string;
  locale: SupportedLocale;
  canManageSubscriptions: boolean;
  onClose: () => void;
  onUpdated: (organization: Organization) => void;
  onOpenStand?: (organizationId: string) => void;
}) {
  const [org, setOrg] = useState(organization),
    [branches, setBranches] = useState<Branch[]>([]),
    [activities, setActivities] = useState<Activity[]>([]),
    [tab, setTab] = useState<
      "details" | "subscription" | "branches" | "history"
    >("details"),
    [message, setMessage] = useState<string | null>(null);
  useEffect(() => {
    if (!message) return;
    const timer = window.setTimeout(() => setMessage(null), 10000);
    return () => window.clearTimeout(timer);
  }, [message]);
  const loadRelated = async () => {
    const [b, a] = await Promise.all([
      supabase
        .from("organization_branches")
        .select("id,name,code,is_headquarters,active,city,phone")
        .eq("organization_id", org.id)
        .order("is_headquarters", { ascending: false }),
      supabase
        .from("organization_activities")
        .select("id,activity_type,subject,body,created_at")
        .eq("organization_id", org.id)
        .order("created_at", { ascending: false }),
    ]);
    setBranches((b.data ?? []) as Branch[]);
    setActivities((a.data ?? []) as Activity[]);
  };
  useEffect(() => {
    void loadRelated();
  }, [org.id]);
  const saveDetails = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const f = new FormData(event.currentTarget),
      payload = {
        name: String(f.get("name")),
        legal_name: String(f.get("legal_name") || "") || null,
        commercial_status: String(f.get("commercial_status")),
        tax_identifier: String(f.get("tax_identifier") || "") || null,
        email: String(f.get("email") || "") || null,
        phone: String(f.get("phone") || "") || null,
        website: normalizeWebsite(f.get("website")),
        address_line: String(f.get("address_line") || "") || null,
        city: String(f.get("city") || "") || null,
        postal_code: String(f.get("postal_code") || "") || null,
        notes: String(f.get("notes") || "") || null,
      };
    const { data, error } = await supabase
      .from("organizations")
      .update(payload)
      .eq("id", org.id)
      .select("*")
      .single();
    if (error || !data) {
      setMessage(
        error?.message ??
          "Não foi possível guardar. Verifique os campos assinalados e tente novamente.",
      );
      return;
    }
    setOrg(data as Organization);
    onUpdated(data as Organization);
    setMessage("Ficha atualizada.");
  };
  const addBranch = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const f = new FormData(event.currentTarget),
      name = String(f.get("branch_name") || "").trim();
    if (!name) return;
    const payload = {
      organization_id: org.id,
      name,
      code: String(f.get("branch_code") || "")
        .trim()
        .toUpperCase(),
      is_headquarters: branches.length === 0,
      country_code: org.country_code,
      city: String(f.get("branch_city") || "") || null,
    };
    const { error } = await supabase
      .from("organization_branches")
      .insert(payload);
    if (!error) {
      event.currentTarget.reset();
      void loadRelated();
    } else setMessage(error.message);
  };
  const addActivity = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const f = new FormData(event.currentTarget),
      subject = String(f.get("subject") || "").trim();
    if (!subject) return;
    const { error } = await supabase.from("organization_activities").insert({
      organization_id: org.id,
      activity_type: String(f.get("activity_type")),
      subject,
      body: String(f.get("body") || "") || null,
      created_by: userId,
    });
    if (!error) {
      event.currentTarget.reset();
      void loadRelated();
    } else setMessage(error.message);
  };
  const archive = async () => {
    const next = org.archived_at
      ? { archived_at: null, archived_by: null, status: "active" }
      : {
          archived_at: new Date().toISOString(),
          archived_by: userId,
          status: "archived",
        };
    const { data, error } = await supabase
      .from("organizations")
      .update(next)
      .eq("id", org.id)
      .select("*")
      .single();
    if (!error && data) {
      setOrg(data as Organization);
      onUpdated(data as Organization);
      setMessage(org.archived_at ? "Stand reativado." : "Stand arquivado.");
    }
  };
  return (
    <div className="modal-backdrop">
      <section className="detail-modal" role="dialog" aria-modal="true">
        <header>
          <div className="org-title">
            <div className="org-avatar">
              {org.name.slice(0, 2).toUpperCase()}
            </div>
            <div>
              <p className="eyebrow">Ficha do stand</p>
              <h2>{org.name}</h2>
              <span>
                {org.country_code} · {org.currency_code} ·{" "}
                {statusLabels[org.commercial_status]?.[locale]}
              </span>
            </div>
          </div>
          <div className="detail-header-actions">
            {onOpenStand && org.commercial_status !== "prospect" && (
              <button
                className="secondary-button"
                type="button"
                onClick={() => onOpenStand(org.id)}
              >
                Abrir espaço do stand
              </button>
            )}
            <button className="icon-button" onClick={onClose}>
              ×
            </button>
          </div>
        </header>
        <nav className="detail-tabs">
          <button
            className={tab === "details" ? "active" : ""}
            onClick={() => setTab("details")}
          >
            Dados
          </button>
          <button
            className={tab === "subscription" ? "active" : ""}
            onClick={() => setTab("subscription")}
          >
            Plano e subscrição
          </button>
          <button
            className={tab === "branches" ? "active" : ""}
            onClick={() => setTab("branches")}
          >
            Filiais
          </button>
          <button
            className={tab === "history" ? "active" : ""}
            onClick={() => setTab("history")}
          >
            Histórico
          </button>
        </nav>
        {message && (
          <button className="notice" role="status" aria-live="polite" onClick={() => setMessage(null)}>
            {message} ×
          </button>
        )}
        {tab === "details" && (
          <form className="detail-body" onSubmit={saveDetails}>
            <div className="form-guidance">
              <strong>Cadastro comercial do stand</strong>
              <span>
                Esta ficha é utilizada pela equipa do Vorno Stand para gerir a
                relação comercial com o stand. Preencha apenas o que souber;
                pode alterar os dados a qualquer momento.
              </span>
            </div>
            <div className="status-guide">
              <details>
                <summary>O que significa cada estado comercial?</summary>
                {Object.keys(statusLabels).map((status) => (
                  <p key={status}>
                    <b>{statusLabels[status][locale]}:</b>{" "}
                    {statusDescriptions[status][locale]}
                  </p>
                ))}
                <p>
                  <b>Nota:</b> “Cancelado” descreve a relação comercial.
                  “Arquivar stand” apenas retira a ficha das listas ativas,
                  preservando os dados e o histórico.
                </p>
              </details>
            </div>
            <div className="form-grid">
              <label>
                <span>Nome comercial</span>
                <input name="name" defaultValue={org.name} />
                <small className="form-hint">
                  Nome utilizado na comunicação com os clientes.
                </small>
              </label>
              <label>
                <span>Nome legal</span>
                <input name="legal_name" defaultValue={org.legal_name ?? ""} />
                <small className="form-hint">
                  Designação oficial registada da empresa.
                </small>
              </label>
              <label>
                <span>Estado comercial com o Vorno Stand</span>
                <select
                  name="commercial_status"
                  defaultValue={org.commercial_status}
                >
                  {Object.keys(statusLabels).map((status) => (
                    <option key={status} value={status}>
                      {statusLabels[status][locale]}
                    </option>
                  ))}
                </select>
                <small className="form-hint">
                  Indica a fase atual da relação entre este stand e a plataforma
                  Vorno Stand.
                </small>
              </label>
              <label>
                <span>{org.country_code === "BR" ? "CNPJ" : "NIPC"}</span>
                <input
                  name="tax_identifier"
                  defaultValue={org.tax_identifier ?? ""}
                  placeholder={
                    org.country_code === "BR"
                      ? "CNPJ da empresa"
                      : "NIPC da empresa"
                  }
                />
                <small className="form-hint">
                  Número de identificação da pessoa coletiva.
                </small>
              </label>
              <label>
                <span>Email</span>
                <input
                  name="email"
                  type="email"
                  defaultValue={org.email ?? ""}
                  placeholder="geral@empresa.pt"
                />
              </label>
              <label>
                <span>Telefone</span>
                <input
                  name="phone"
                  defaultValue={org.phone ?? ""}
                  placeholder={org.country_code === "BR" ? "+55 …" : "+351 …"}
                />
              </label>
              <label>
                <span>Website</span>
                <input
                  name="website"
                  type="text"
                  inputMode="url"
                  defaultValue={org.website ?? ""}
                  placeholder="Ex.: nome-do-stand.pt"
                />
                <small className="form-hint">
                  Pode escrever apenas o domínio. O sistema completa o endereço.
                </small>
              </label>
              <label>
                <span>Localidade</span>
                <input name="city" defaultValue={org.city ?? ""} />
              </label>
              <label>
                <span>Código postal</span>
                <input
                  name="postal_code"
                  defaultValue={org.postal_code ?? ""}
                />
              </label>
              <label className="wide">
                <span>Morada</span>
                <input
                  name="address_line"
                  defaultValue={org.address_line ?? ""}
                  placeholder="Rua, número e complemento"
                />
              </label>
              <label className="wide">
                <span>Notas internas</span>
                <textarea
                  name="notes"
                  rows={4}
                  defaultValue={org.notes ?? ""}
                  placeholder="Informação útil para a equipa acompanhar este stand"
                />
                <small className="form-hint">
                  Estas notas destinam-se à equipa e não ficam visíveis no
                  website público.
                </small>
              </label>
            </div>
            <footer>
              <button
                className="danger-button"
                type="button"
                onClick={() => void archive()}
              >
                {org.archived_at ? "Reativar stand" : "Arquivar stand"}
              </button>
              <button className="primary-button compact">
                Guardar
              </button>
            </footer>
          </form>
        )}
        {tab === "subscription" && (
          <SubscriptionPanel
            organization={org}
            locale={locale}
            canManage={canManageSubscriptions}
            onOrganizationUpdated={(updated) => {
              setOrg(updated);
              onUpdated(updated);
            }}
          />
        )}
        {tab === "branches" && (
          <div className="detail-body">
            <p className="tab-context">
              <strong>{branches.length}</strong>{" "}
              {branches.length === 1
                ? "filial registada"
                : "filiais registadas"}
            </p>
            <form className="inline-create" onSubmit={addBranch}>
              <input name="branch_name" placeholder="Nome da filial" required />
              <input
                name="branch_code"
                placeholder="Código (ex.: SEDE)"
                required
              />
              <input name="branch_city" placeholder="Localidade" />
              <button className="primary-button compact">Adicionar</button>
            </form>
            <div className="mini-list">
              {branches.map((branch) => (
                <article key={branch.id}>
                  <strong>{branch.name}</strong>
                  <span>
                    {branch.code} · {branch.city || org.country_code}
                  </span>
                  {branch.is_headquarters && <b>Sede</b>}
                </article>
              ))}
            </div>
          </div>
        )}
        {tab === "history" && (
          <div className="detail-body">
            <p className="tab-context">
              <strong>{activities.length}</strong>{" "}
              {activities.length === 1
                ? "registo no histórico"
                : "registos no histórico"}
            </p>
            <form className="activity-create" onSubmit={addActivity}>
              <select name="activity_type">
                <option value="note">Nota</option>
                <option value="call">Chamada</option>
                <option value="email">Email</option>
                <option value="meeting">Reunião</option>
                <option value="follow_up">Acompanhamento</option>
              </select>
              <input name="subject" placeholder="Assunto" required />
              <textarea
                name="body"
                placeholder="Registo do contacto"
                rows={2}
              />
              <button className="primary-button compact">Registar</button>
            </form>
            <div className="timeline">
              {activities.map((item) => (
                <article key={item.id}>
                  <i />
                  <div>
                    <strong>{item.subject}</strong>
                    <p>{item.body || item.activity_type}</p>
                    <time>{formatDate(item.created_at, locale)}</time>
                  </div>
                </article>
              ))}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

type NavigationIconName =
  | "overview"
  | "commercial"
  | "clients"
  | "users"
  | "plans"
  | "templates"
  | "versions"
  | "support"
  | "settings";

const navigationIconPaths: Record<NavigationIconName, ReactNode> = {
  overview: (
    <>
      <rect x="3" y="3" width="7" height="7" />
      <rect x="14" y="3" width="7" height="7" />
      <rect x="3" y="14" width="7" height="7" />
      <rect x="14" y="14" width="7" height="7" />
    </>
  ),
  commercial: (
    <>
      <path d="M4 19V9" />
      <path d="M10 19V5" />
      <path d="M16 19v-7" />
      <path d="m3 8 6-5 6 7 6-5" />
    </>
  ),
  clients: (
    <>
      <path d="M3 10h18" />
      <path d="m5 10 1-6h12l1 6" />
      <path d="M5 10v10h14V10" />
      <path d="M8 20v-6h4v6" />
    </>
  ),
  users: (
    <>
      <circle cx="9" cy="8" r="4" />
      <path d="M3 21v-2a6 6 0 0 1 12 0v2" />
      <path d="M16 4a4 4 0 0 1 0 8" />
      <path d="M17 15a6 6 0 0 1 4 6" />
    </>
  ),
  plans: (
    <>
      <rect x="4" y="3" width="16" height="18" rx="1" />
      <path d="M8 8h8" />
      <path d="M8 12h8" />
      <path d="M8 16h5" />
    </>
  ),
  templates: (
    <>
      <rect x="3" y="3" width="18" height="18" rx="1" />
      <path d="M3 9h18" />
      <path d="M9 9v12" />
    </>
  ),
  versions: (
    <>
      <path d="M12 3v6l4 2" />
      <circle cx="12" cy="12" r="9" />
      <path d="m5 5-2 4 4 1" />
    </>
  ),
  support: (
    <>
      <path d="M4 5h16v12H9l-5 3z" />
      <path d="M8 9h8M8 13h5" />
    </>
  ),
  settings: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1-2.8 2.8-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.6v.2h-4V21a1.7 1.7 0 0 0-1-1.6 1.7 1.7 0 0 0-1.9.3l-.1.1L4.2 17l.1-.1a1.7 1.7 0 0 0 .3-1.9A1.7 1.7 0 0 0 3 14H3v-4h.1a1.7 1.7 0 0 0 1.6-1 1.7 1.7 0 0 0-.3-1.9L4.2 7 7 4.2l.1.1A1.7 1.7 0 0 0 9 4.6 1.7 1.7 0 0 0 10 3V3h4v.1a1.7 1.7 0 0 0 1 1.6 1.7 1.7 0 0 0 1.9-.3l.1-.1L19.8 7l-.1.1a1.7 1.7 0 0 0-.3 1.9 1.7 1.7 0 0 0 1.6 1h.2v4H21a1.7 1.7 0 0 0-1.6 1Z" />
    </>
  ),
};

function NavigationIcon({ name }: { name: NavigationIconName }) {
  return (
    <svg
      className="navigation-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {navigationIconPaths[name]}
    </svg>
  );
}

export function Dashboard({
  profile,
  membership,
  onSignOut,
  onProfileUpdated,
  onOpenStand,
}: {
  profile: DashboardProfile;
  membership: DashboardMembership;
  onSignOut: () => void;
  onProfileUpdated: (profile: DashboardProfile) => void;
  onOpenStand: (organizationId: string) => void;
}) {
  const [view, setView] = useState<View>("overview"),
    [settingsTab, setSettingsTab] = useState<"localization" | "templates" | "documents">("localization"),
    [organizations, setOrganizations] = useState<Organization[]>([]),
    [loading, setLoading] = useState(true),
    [showCreate, setShowCreate] = useState(false),
    [selectedOrganization, setSelectedOrganization] =
      useState<Organization | null>(null),
    [search, setSearch] = useState(""),
    [statusFilter, setStatusFilter] = useState("all"),
    [countryFilter, setCountryFilter] = useState("all"),
    [notice, setNotice] = useState<string | null>(null),
    [profileOpen, setProfileOpen] = useState(false),
    locale = profile.locale,
    t = copy(locale);
  const isSuperMaster = membership.role_key === "super_master";
  const canManageCommercial =
    isSuperMaster ||
    membership.role_key === "commercial_manager" ||
    membership.role_key === "commercial" ||
    (membership.role_key === "platform_master" &&
      membership.permissions.includes("commercial.manage"));
  const canViewClientDirectory =
    isSuperMaster ||
    (membership.role_key === "platform_master" &&
      (membership.permissions.includes("commercial.manage") ||
        membership.permissions.includes("plans.manage")));
  const scopedCommercialOverview =
    membership.role_key === "commercial_manager" ||
    membership.role_key === "commercial";
  const canManageUsers =
    isSuperMaster ||
    (membership.role_key === "platform_master" &&
      membership.permissions.includes("users.manage"));
  const canManagePlans =
    isSuperMaster ||
    (membership.role_key === "platform_master" &&
      membership.permissions.includes("plans.manage"));
  const canManageVersions =
    isSuperMaster ||
    (membership.role_key === "platform_master" &&
      membership.permissions.includes("versions.manage"));
  const canManageSupport = membership.permissions.includes("support.manage");
  useEffect(() => {
    if (!notice) return;
    const timer = window.setTimeout(() => setNotice(null), 10000);
    return () => window.clearTimeout(timer);
  }, [notice]);
  const loadOrganizations = () =>
    supabase
      .from("organizations")
      .select("*")
      .order("created_at", { ascending: false })
      .then(({ data }) => {
        setOrganizations((data ?? []) as Organization[]);
        setLoading(false);
      });
  useEffect(() => {
    void loadOrganizations();
    const refresh = () => void loadOrganizations();
    window.addEventListener("vorno:organizations-changed", refresh);
    const channel = supabase
      .channel(`platform-organizations-${profile.user_id}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "organizations" },
        refresh,
      )
      .subscribe();
    return () => {
      window.removeEventListener("vorno:organizations-changed", refresh);
      void supabase.removeChannel(channel);
    };
  }, []);
  const totals = useMemo(
    () => ({
      all: organizations.length,
      prospects: organizations.filter((o) => o.commercial_status === "prospect")
        .length,
      customers: organizations.filter((o) => o.commercial_status !== "prospect")
        .length,
    }),
    [organizations],
  );
  const filteredOrganizations = useMemo(
    () =>
      organizations.filter(
        (org) =>
          (!search ||
            `${org.name} ${org.legal_name ?? ""} ${org.email ?? ""}`
              .toLowerCase()
              .includes(search.toLowerCase())) &&
          (statusFilter === "all" || org.commercial_status === statusFilter) &&
          (countryFilter === "all" || org.country_code === countryFilter),
      ),
    [organizations, search, statusFilter, countryFilter],
  );
  const selectCountry = async (country: SupportedCountry) => {
    const region = regionalDefaults[country],
      { data, error } = await supabase
        .from("profiles")
        .update({
          country_code: country,
          locale: region.locale,
          currency_code: region.currencyCode,
          timezone: region.timezone,
        })
        .eq("user_id", profile.user_id)
        .select(
          "user_id,display_name,locale,country_code,currency_code,timezone",
        )
        .single();
    if (!error && data) {
      onProfileUpdated(data as DashboardProfile);
      setNotice(copy(region.locale).localeSaved);
    }
  };
  const nav = (target: View, label: string, icon: NavigationIconName) => (
    <button
      className={view === target ? "active" : ""}
      onClick={() => setView(target)}
      aria-label={label}
      title={label}
    >
      <NavigationIcon name={icon} />
      <span>{label}</span>
    </button>
  );
  return (
    <div className="dashboard-shell">
      <aside className="dashboard-sidebar">
        <Brand />
        <nav>
          {nav("overview", t.overview, "overview")}
          {canManageCommercial && nav("commercial", "Comercial", "commercial")}
          {canViewClientDirectory &&
            nav(
              "clients",
              locale === "pt-BR" ? "Lojas clientes" : "Stands clientes",
              "clients",
            )}
          {canManageUsers && nav("users", t.users, "users")}
          {canManagePlans && nav("plans", t.plans, "plans")}
          {canManageVersions && nav("versions", t.versions, "versions")}
          {canManageSupport && nav("support", "Suporte", "support")}
          {isSuperMaster && nav("operations", "Operação", "versions")}
          {nav("settings", "Configurações", "settings")}
        </nav>
        <div className="sidebar-lower">
          <div className={`sidebar-profile ${profileOpen ? "open" : ""}`}>
            <button
              className="profile-avatar-button"
              aria-label="Mostrar informações do perfil"
              aria-expanded={profileOpen}
              onClick={() => setProfileOpen((open) => !open)}
            >
              <span className="avatar">M</span>
            </button>
            <div className="profile-information">
              <div className="profile-name">
                <strong>{profile.display_name ?? "Master"}</strong>
                <small>
                  {membership.role_key === "super_master"
                    ? "Super Master"
                    : membership.role_key === "platform_master"
                      ? "Master da plataforma"
                      : membership.role_key === "commercial_manager"
                        ? "Gestor Comercial"
                        : "Comercial"}
                </small>
              </div>
              <div className="profile-details">
                <span className="profile-region">
                  {profile.country_code} · {profile.currency_code}
                </span>
                <em className="profile-environment">
                  <i /> {environment === "testes" ? "Testes" : environment}
                </em>
              </div>
            </div>
            <div className="profile-session">
              <span>
                Sessão <b>ON</b>
              </span>
              <button
                type="button"
                onClick={onSignOut}
                title="Terminar sessão"
                aria-label="Terminar sessão"
              >
                <i aria-hidden="true">↗</i>Sair
              </button>
            </div>
          </div>
        </div>
      </aside>
      <main className="dashboard-main">
        <header>
          <div>
            <p className="eyebrow">{view === "settings" ? "Configurações globais" : t.centralAdmin}</p>
            <h1>
              {view === "commercial"
                ? "Comercial"
                : view === "clients"
                  ? locale === "pt-BR"
                    ? "Lojas clientes"
                    : "Stands clientes"
                  : view === "users"
                    ? t.users
                    : view === "plans"
                      ? t.plans
                      : view === "versions"
                        ? t.versions
                      : view === "support"
                        ? "Suporte"
                      : view === "operations"
                        ? "Operação"
                      : view === "settings"
                        ? "Configurações"
                        : t.overview}
            </h1>
          </div>
          <div className="dashboard-header-actions">
            <ThemeToggle />
            <div className="header-task-center">
              <TaskCenter
                userId={profile.user_id}
                isMaster={
                  isSuperMaster ||
                  membership.role_key === "commercial_manager" ||
                  (membership.role_key === "platform_master" &&
                    membership.permissions.includes("commercial.manage"))
                }
              />
            </div>
          </div>
        </header>
        {notice && (
          <button className="notice" role="status" aria-live="polite" onClick={() => setNotice(null)}>
            {notice} ×
          </button>
        )}
        {view === "overview" && (
          <>
            <section className="welcome-panel">
              <div>
                <span className="live-badge">
                  {membership.role_key === "commercial_manager"
                    ? "Panorama da equipa"
                    : membership.role_key === "commercial"
                      ? "O meu panorama comercial"
                      : "Panorama da plataforma"}
                </span>
                <h2>
                  {profile.display_name ?? "Master"},{" "}
                  {scopedCommercialOverview
                    ? "acompanhe a operação autorizada."
                    : "acompanhe toda a operação."}
                </h2>
                <p>
                  {scopedCommercialOverview
                    ? "Aqui aparecem apenas os stands, leads e tarefas pertencentes ao seu âmbito comercial."
                    : "Visão global de clientes, atividade comercial, tarefas e estado do ecossistema Vorno Stand."}
                </p>
              </div>
              <div className="permission-card">
                <small>Âmbito de acesso</small>
                <strong>
                  {membership.role_key === "super_master"
                    ? "Toda a plataforma"
                    : membership.role_key === "commercial_manager"
                      ? "Equipa comercial atribuída"
                      : membership.role_key === "commercial"
                        ? "Registos comerciais próprios"
                        : "Gestão da plataforma"}
                </strong>
                <span>
                  {profile.locale} · {profile.timezone}
                </span>
              </div>
            </section>
            <section className="metric-grid">
              <article>
                <small>{scopedCommercialOverview ? "Stands no âmbito" : "Stands registados"}</small>
                <strong>{totals.all}</strong>
                <span>{scopedCommercialOverview ? "Ligados aos leads autorizados" : "Portugal + Brasil"}</span>
              </article>
              <article>
                <small>Em prospeção</small>
                <strong>{totals.prospects}</strong>
                <span>Geridos na área Comercial</span>
              </article>
              <article>
                <small>Stands clientes</small>
                <strong>{totals.customers}</strong>
                <span>Com relação ativa</span>
              </article>
            </section>
          </>
        )}
        {view === "commercial" && (
          <CommercialWorkspace
            userId={profile.user_id}
            locale={locale}
            isMaster={
              isSuperMaster ||
              membership.role_key === "commercial_manager" ||
              (membership.role_key === "platform_master" &&
                membership.permissions.includes("commercial.manage"))
            }
            hasGlobalScope={canViewClientDirectory}
            canEditTeams={isSuperMaster ||
              (membership.role_key === "platform_master" &&
                membership.permissions.includes("commercial.manage"))}
          />
        )}
        {view === "users" && (
          <UserManagement
            isSuperMaster={isSuperMaster}
            canManage={canManageUsers}
          />
        )}
        {view === "plans" && (
          <PlanManagement
            canManage={canManagePlans}
          />
        )}
        {view === "versions" && <VersionCenter />}
        {view === "support" && <PlatformSupportCenter />}
        {view === "operations" && <PlatformOperationsCenter />}
        {view === "clients" && (
          <section className="crm-section">
            <div className="section-toolbar">
              <div>
                <p>
                  {totals.customers}{" "}
                  {locale === "pt-BR" ? "lojas clientes" : "stands clientes"}
                </p>
                <small>
                  Empresas já convertidas em clientes, com ou sem acesso operacional ativo.
                </small>
              </div>
            </div>
            <div className="crm-filters">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Pesquisar por nome, email ou entidade…"
              />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">Todos os estados</option>
                <option value="trial">Em período experimental</option>
                <option value="customer">Cliente</option>
                <option value="suspended">Suspenso</option>
                <option value="cancelled">Cancelado</option>
              </select>
              <select
                value={countryFilter}
                onChange={(e) => setCountryFilter(e.target.value)}
              >
                <option value="all">Todos os países</option>
                <option value="PT">Portugal</option>
                <option value="BR">Brasil</option>
              </select>
            </div>
            {loading ? (
              <div className="empty-state">A carregar…</div>
            ) : filteredOrganizations.filter(
                (org) => org.commercial_status !== "prospect",
              ).length === 0 ? (
              <div className="empty-state">
                <strong>Ainda não existem stands clientes.</strong>
                <span>
                  Converta um lead na área Comercial quando existir um acordo ou
                  período experimental.
                </span>
              </div>
            ) : (
              <div className="organization-list">
                {filteredOrganizations
                  .filter((org) => org.commercial_status !== "prospect")
                  .map((org) => (
                    <article
                      key={org.id}
                      className={org.archived_at ? "archived" : ""}
                      role="button"
                      tabIndex={0}
                      aria-label={`Abrir perfil de ${org.name}`}
                      onClick={() => setSelectedOrganization(org)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          setSelectedOrganization(org);
                        }
                      }}
                    >
                      <div className="org-avatar">
                        {org.name.slice(0, 2).toUpperCase()}
                      </div>
                      <div>
                        <strong>{org.name}</strong>
                        <span>
                          {org.city || org.country_code} · {org.currency_code}
                        </span>
                      </div>
                      <span
                        className={`status status-${org.commercial_status}`}
                      >
                        {statusLabels[org.commercial_status]?.[locale] ??
                          org.commercial_status}
                      </span>
                      <div className="org-contact">
                        <span>{org.email || "—"}</span>
                        <small>
                          {org.phone || org.website || "Sem contacto"}
                        </small>
                      </div>
                      <time>{formatDate(org.created_at, locale)}</time>
                    </article>
                  ))}
              </div>
            )}
          </section>
        )}
        {view === "settings" && (
          <section className="platform-settings">
            <header><div><p className="eyebrow">Estrutura da plataforma</p><h2>Configurações do Stand</h2><p>Definições globais, estáveis e partilhadas pelo sistema. A operação diária continua nas respetivas ferramentas.</p></div></header>
            <nav className="settings-tabs" aria-label="Temas das configurações globais">
              <button className={settingsTab==="localization"?"active":""} onClick={()=>setSettingsTab("localization")}>País, idioma e moeda</button>
              {isSuperMaster&&<button className={settingsTab==="templates"?"active":""} onClick={()=>setSettingsTab("templates")}>Templates dos sites</button>}
              {isSuperMaster&&<button className={settingsTab==="documents"?"active":""} onClick={()=>setSettingsTab("documents")}>Documentos Default</button>}
            </nav>
            {settingsTab==="localization"&&<section className="settings-panel">
              <p className="eyebrow">Localização</p><h2>{t.country}, {t.language} e {t.currency}</h2>
              <p>O país define automaticamente a experiência inicial. Cada stand mantém a sua própria configuração regional.</p>
              <div className="country-options">{(["PT", "BR"] as SupportedCountry[]).map((code) => {const region = regionalDefaults[code];return <button key={code} className={profile.country_code === code ? "selected" : ""} onClick={() => void selectCountry(code)}><strong>{region.countryName}</strong><span>{region.locale} · {region.currencyCode}</span><small>{region.timezone}</small><em>{formatCurrency(1250, region.locale, region.currencyCode)}</em></button>;})}</div>
            </section>}
            {settingsTab==="templates"&&isSuperMaster&&<SiteTemplateCatalog />}
            {settingsTab==="documents"&&isSuperMaster&&<PlatformDefaultDocuments />}
          </section>
        )}
      </main>
      {showCreate && (
        <OrganizationForm
          locale={locale}
          onClose={() => setShowCreate(false)}
          onCreated={(organization) => {
            setOrganizations((current) => [organization, ...current]);
            setShowCreate(false);
            setSelectedOrganization(organization);
            setView("clients");
          }}
        />
      )}
      {selectedOrganization && (
        <OrganizationDetail
          organization={selectedOrganization}
          userId={profile.user_id}
          locale={locale}
          canManageSubscriptions={
            canManagePlans
          }
          onClose={() => setSelectedOrganization(null)}
          onOpenStand={
            isSuperMaster ? onOpenStand : undefined
          }
          onUpdated={(updated) => {
            setSelectedOrganization(updated);
            setOrganizations((current) =>
              current.map((org) => (org.id === updated.id ? updated : org)),
            );
          }}
        />
      )}
    </div>
  );
}
